import { jsPDF } from "jspdf";
import type { PropertyTransactionFormData } from "../PropertyTransactionForm";

// A4 dimensions in mm
const PAGE_W = 210;
const MARGIN_X = 15;
const MARGIN_TOP = 10;
const USABLE_W = PAGE_W - 2 * MARGIN_X; // 180mm

// Colors
const HEADING_BG = [20, 28, 73]; // brand #141C49 — Master Navy
const DIVIDER_COLOR = [200, 200, 200];
const FIELD_BG = [255, 255, 255];
const FIELD_BORDER = [180, 180, 180];
const SOLAR_BG = [198, 146, 20]; // One Solar Gold

// Field sizing
const FIELD_H = 5;
const LABEL_FONT_SIZE = 7;
const FIELD_FONT_SIZE = 8;
const LABEL_OFFSET = 3;
const ROW_ADVANCE = LABEL_OFFSET + FIELD_H + 2; // 10mm

let cursorY = MARGIN_TOP;

function resetCursor() {
  cursorY = MARGIN_TOP;
}

function advanceCursor(amount: number) {
  cursorY += amount;
}

function drawSectionHeading(doc: jsPDF, text: string) {
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.rect(MARGIN_X, cursorY, USABLE_W, 5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text(text, MARGIN_X + 2, cursorY + 3.5);
  doc.setTextColor(0, 0, 0);
  advanceCursor(6);
}

function drawDivider(doc: jsPDF) {
  doc.setDrawColor(DIVIDER_COLOR[0], DIVIDER_COLOR[1], DIVIDER_COLOR[2]);
  doc.setLineWidth(0.3);
  doc.line(MARGIN_X, cursorY, MARGIN_X + USABLE_W, cursorY);
  advanceCursor(2);
}

function addLabeledTextField(
  doc: jsPDF,
  label: string,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number = FIELD_H
) {
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(60, 60, 60);
  doc.text(label, x, y - 1);
  doc.setTextColor(0, 0, 0);

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

function addInlineTextField(
  doc: jsPDF,
  fieldName: string,
  value: string,
  x: number,
  y: number,
  width: number,
  height: number = FIELD_H
) {
  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, width, height, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tf = new (doc as any).AcroFormTextField();
  tf.fieldName = fieldName;
  tf.x = x;
  tf.y = y;
  tf.width = width;
  tf.height = height;
  tf.fontSize = FIELD_FONT_SIZE;
  tf.fontName = "Helvetica";
  tf.defaultValue = value;
  tf.value = value;
  doc.addField(tf);
}

function addCheckboxField(
  doc: jsPDF,
  label: string,
  fieldName: string,
  checked: boolean,
  x: number,
  y: number
) {
  const cbSize = 3.5;

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(x, y, cbSize, cbSize, "FD");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cb = new (doc as any).AcroFormCheckBox();
  cb.fieldName = fieldName;
  cb.x = x;
  cb.y = y;
  cb.width = cbSize;
  cb.height = cbSize;
  if (checked) {
    cb.appearanceState = "On";
    cb.value = "On";
    cb.defaultValue = "On";
  } else {
    cb.appearanceState = "Off";
    cb.value = "Off";
    cb.defaultValue = "Off";
  }
  doc.addField(cb);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(label, x + cbSize + 1.5, y + 2.8);
}

async function loadLogoAsBase64(): Promise<string | null> {
  try {
    const response = await fetch("/assets/One-logo.png");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function addPageHeader(doc: jsPDF, logoBase64: string | null, subtitle: string) {
  resetCursor();
  if (logoBase64) {
    const logoW = 15;
    const logoH = logoW * (330 / 294);
    doc.addImage(logoBase64, "PNG", MARGIN_X, cursorY, logoW, logoH);
  }

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("PROPERTY TRANSACTION & MORTGAGE FORM", PAGE_W / 2, cursorY + 6, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.text(subtitle, PAGE_W / 2, cursorY + 10, { align: "center" });
  doc.setTextColor(0, 0, 0);

  const rightX = MARGIN_X + USABLE_W;
  doc.setFontSize(5.5);
  doc.setFont("helvetica", "bold");
  doc.text("One Property Australia", rightX, cursorY + 2.5, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.text("ACN: 664 898 846", rightX, cursorY + 5, { align: "right" });
  doc.text("6/22 Alexandra Pl, Murarrie QLD 4172", rightX, cursorY + 7.5, { align: "right" });
  doc.text("ONEPROPERTYAUSTRALIA.COM.AU", rightX, cursorY + 10, { align: "right" });

  advanceCursor(16);
  drawDivider(doc);
}

export async function generatePropertyTransactionPDF(
  formData: PropertyTransactionFormData
): Promise<void> {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const logoBase64 = await loadLogoAsBase64();

  const gap = 3;
  const lo = LABEL_OFFSET;

  // ══════════════════════════════════════════
  // PAGE 1 — APPLICANT DETAILS + PROPERTY + PURCHASE
  // ══════════════════════════════════════════
  addPageHeader(doc, logoBase64, "Direct Investment & SMSF — Page 1 of 4");

  // ─── SECTION 1: APPLICANT DETAILS ───
  drawSectionHeading(doc, "SECTION 1 — APPLICANT DETAILS");

  const colGap = 6;
  const colW = (USABLE_W - colGap) / 2; // ~87mm
  const colAx = MARGIN_X;
  const colBx = MARGIN_X + colW + colGap;

  // Applicant sub-labels
  doc.setFontSize(LABEL_FONT_SIZE + 0.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("Applicant A", colAx, cursorY + 3);
  doc.text("Applicant B", colBx, cursorY + 3);
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  // Name / DOB / Marital Status
  addLabeledTextField(doc, "Full Name:", "nameA", formData.nameA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Full Name:", "nameB", formData.nameB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  const sub3 = (colW - 2 * 2) / 3;
  addLabeledTextField(doc, "DOB:", "dobA", formData.dobA, colAx, cursorY + lo, sub3);
  addLabeledTextField(doc, "Marital:", "maritalStatusA", formData.maritalStatusA, colAx + sub3 + 2, cursorY + lo, sub3 * 2 + 2);
  addLabeledTextField(doc, "DOB:", "dobB", formData.dobB, colBx, cursorY + lo, sub3);
  addLabeledTextField(doc, "Marital:", "maritalStatusB", formData.maritalStatusB, colBx + sub3 + 2, cursorY + lo, sub3 * 2 + 2);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Residential Address:", "addressA", formData.addressA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Residential Address:", "addressB", formData.addressB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Yrs at Address:", "residenceYearsA", formData.residenceYearsA, colAx, cursorY + lo, sub3);
  addLabeledTextField(doc, "State:", "stateA", formData.stateA, colAx + sub3 + 2, cursorY + lo, sub3);
  addLabeledTextField(doc, "P/Code:", "postcodeA", formData.postcodeA, colAx + 2 * (sub3 + 2), cursorY + lo, sub3);
  addLabeledTextField(doc, "Yrs at Address:", "residenceYearsB", formData.residenceYearsB, colBx, cursorY + lo, sub3);
  addLabeledTextField(doc, "State:", "stateB", formData.stateB, colBx + sub3 + 2, cursorY + lo, sub3);
  addLabeledTextField(doc, "P/Code:", "postcodeB", formData.postcodeB, colBx + 2 * (sub3 + 2), cursorY + lo, sub3);
  advanceCursor(ROW_ADVANCE);

  const half2 = (colW - 2) / 2;
  addLabeledTextField(doc, "Ph (H):", "phoneHomeA", formData.phoneHomeA, colAx, cursorY + lo, half2);
  addLabeledTextField(doc, "Mobile:", "mobileA", formData.mobileA, colAx + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "Ph (H):", "phoneHomeB", formData.phoneHomeB, colBx, cursorY + lo, half2);
  addLabeledTextField(doc, "Mobile:", "mobileB", formData.mobileB, colBx + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Email:", "emailA", formData.emailA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Email:", "emailB", formData.emailB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "TFN:", "tfnA", formData.tfnA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "TFN:", "tfnB", formData.tfnB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Occupation:", "occupationA", formData.occupationA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Occupation:", "occupationB", formData.occupationB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Employer:", "employerA", formData.employerA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Employer:", "employerB", formData.employerB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Employer Address:", "employerAddressA", formData.employerAddressA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Employer Address:", "employerAddressB", formData.employerAddressB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  // Employment type checkboxes
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Employment:", colAx, cursorY + 3.5);
  addCheckboxField(doc, "F/T", "empFITA", formData.empFITA, colAx + 22, cursorY);
  addCheckboxField(doc, "P/T", "empPTA", formData.empPTA, colAx + 33, cursorY);
  addCheckboxField(doc, "Casual", "empCasualA", formData.empCasualA, colAx + 44, cursorY);
  addCheckboxField(doc, "Self-Emp", "empSelfA", formData.empSelfA, colAx + 58, cursorY);
  doc.text("Employment:", colBx, cursorY + 3.5);
  addCheckboxField(doc, "F/T", "empFITB", formData.empFITB, colBx + 22, cursorY);
  addCheckboxField(doc, "P/T", "empPTB", formData.empPTB, colBx + 33, cursorY);
  addCheckboxField(doc, "Casual", "empCasualB", formData.empCasualB, colBx + 44, cursorY);
  addCheckboxField(doc, "Self-Emp", "empSelfB", formData.empSelfB, colBx + 58, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Yrs in Role:", "employmentYearsA", formData.employmentYearsA, colAx, cursorY + lo, half2);
  addLabeledTextField(doc, "Gross Income P/A $:", "grossIncomeA", formData.grossIncomeA, colAx + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "Yrs in Role:", "employmentYearsB", formData.employmentYearsB, colBx, cursorY + lo, half2);
  addLabeledTextField(doc, "Gross Income P/A $:", "grossIncomeB", formData.grossIncomeB, colBx + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Other Income $:", "otherIncomeA", formData.otherIncomeA, colAx, cursorY + lo, half2);
  addLabeledTextField(doc, "Income Type:", "otherIncomeTypeA", formData.otherIncomeTypeA, colAx + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "Other Income $:", "otherIncomeB", formData.otherIncomeB, colBx, cursorY + lo, half2);
  addLabeledTextField(doc, "Income Type:", "otherIncomeTypeB", formData.otherIncomeTypeB, colBx + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Business Name (SE):", "selfEmpBusinessNameA", formData.selfEmpBusinessNameA, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Business Name (SE):", "selfEmpBusinessNameB", formData.selfEmpBusinessNameB, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "ABN:", "selfEmpABNA", formData.selfEmpABNA, colAx, cursorY + lo, half2);
  addLabeledTextField(doc, "Yrs in Business:", "selfEmpYearsA", formData.selfEmpYearsA, colAx + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "ABN:", "selfEmpABNB", formData.selfEmpABNB, colBx, cursorY + lo, half2);
  addLabeledTextField(doc, "Yrs in Business:", "selfEmpYearsB", formData.selfEmpYearsB, colBx + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Dependents:", "dependentsA", formData.dependentsA, colAx, cursorY + lo, half2);
  addLabeledTextField(doc, "Ages:", "dependentAgesA", formData.dependentAgesA, colAx + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "Dependents:", "dependentsB", formData.dependentsB, colBx, cursorY + lo, half2);
  addLabeledTextField(doc, "Ages:", "dependentAgesB", formData.dependentAgesB, colBx + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE + 1);

  drawDivider(doc);

  // ─── SECTION 2: PROPERTY DETAILS ───
  drawSectionHeading(doc, "SECTION 2 — PROPERTY DETAILS");

  addLabeledTextField(doc, "Property Address:", "propertyAddress", formData.propertyAddress, MARGIN_X, cursorY + lo, USABLE_W);
  advanceCursor(ROW_ADVANCE);

  const propSub3 = (USABLE_W - 2 * 3) / 3;
  addLabeledTextField(doc, "Suburb:", "propertySuburb", formData.propertySuburb, MARGIN_X, cursorY + lo, propSub3);
  addLabeledTextField(doc, "State:", "propertyState", formData.propertyState, MARGIN_X + propSub3 + 3, cursorY + lo, propSub3);
  addLabeledTextField(doc, "Postcode:", "propertyPostcode", formData.propertyPostcode, MARGIN_X + 2 * (propSub3 + 3), cursorY + lo, propSub3);
  advanceCursor(ROW_ADVANCE);

  // Property type checkboxes
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Property Type:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "House", "isHouse", formData.isHouse, MARGIN_X + 26, cursorY);
  addCheckboxField(doc, "Unit", "isUnit", formData.isUnit, MARGIN_X + 43, cursorY);
  addCheckboxField(doc, "Townhouse", "isTownhouse", formData.isTownhouse, MARGIN_X + 55, cursorY);
  addCheckboxField(doc, "Duplex", "isDuplex", formData.isDuplex, MARGIN_X + 75, cursorY);
  addCheckboxField(doc, "Land", "isLand", formData.isLand, MARGIN_X + 91, cursorY);
  addCheckboxField(doc, "Commercial", "isCommercial", formData.isCommercial, MARGIN_X + 103, cursorY);
  advanceCursor(7);

  const propSub5 = (USABLE_W - 4 * 2) / 5;
  addLabeledTextField(doc, "Bedrooms:", "bedrooms", formData.bedrooms, MARGIN_X, cursorY + lo, propSub5);
  addLabeledTextField(doc, "Bathrooms:", "bathrooms", formData.bathrooms, MARGIN_X + propSub5 + 2, cursorY + lo, propSub5);
  addLabeledTextField(doc, "Car Spaces:", "carSpaces", formData.carSpaces, MARGIN_X + 2 * (propSub5 + 2), cursorY + lo, propSub5);
  addLabeledTextField(doc, "Land Size (m²):", "landSize", formData.landSize, MARGIN_X + 3 * (propSub5 + 2), cursorY + lo, propSub5);
  addLabeledTextField(doc, "Floor Area (m²):", "floorArea", formData.floorArea, MARGIN_X + 4 * (propSub5 + 2), cursorY + lo, propSub5);
  advanceCursor(ROW_ADVANCE);

  const propHalf = (USABLE_W - gap) / 2;
  addLabeledTextField(doc, "Year Built:", "yearBuilt", formData.yearBuilt, MARGIN_X, cursorY + lo, propSub5);
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Body Corp:", MARGIN_X + propSub5 + 5, cursorY + 3.5);
  addCheckboxField(doc, "YES", "bodyCorpYes", formData.bodyCorpYes, MARGIN_X + propSub5 + 22, cursorY);
  addCheckboxField(doc, "NO", "bodyCorpNo", formData.bodyCorpNo, MARGIN_X + propSub5 + 32, cursorY);
  addLabeledTextField(doc, "Body Corp Fees P/Q $:", "bodyCorpFees", formData.bodyCorpFees, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE + 1);

  drawDivider(doc);

  // ─── SECTION 3: PURCHASE DETAILS & PURPOSE ───
  drawSectionHeading(doc, "SECTION 3 — PURCHASE DETAILS & PURPOSE");

  addLabeledTextField(doc, "Purchase Price $:", "purchasePrice", formData.purchasePrice, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Deposit Amount $:", "depositAmount", formData.depositAmount, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Deposit Paid:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "depositPaidYes", formData.depositPaidYes, MARGIN_X + 22, cursorY);
  addCheckboxField(doc, "NO", "depositPaidNo", formData.depositPaidNo, MARGIN_X + 32, cursorY);
  doc.text("Deposit Source:", MARGIN_X + 50, cursorY + 3.5);
  addCheckboxField(doc, "Savings", "depositCash", formData.depositCash, MARGIN_X + 72, cursorY);
  addCheckboxField(doc, "Equity", "depositEquity", formData.depositEquity, MARGIN_X + 86, cursorY);
  addCheckboxField(doc, "FHSS", "depositFHSS", formData.depositFHSS, MARGIN_X + 99, cursorY);
  addCheckboxField(doc, "Gift", "depositGift", formData.depositGift, MARGIN_X + 110, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Exchange Date:", "exchangeDate", formData.exchangeDate, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Settlement Date:", "settlementDate", formData.settlementDate, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("First Home Buyer:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "firstHomeBuyerYes", formData.firstHomeBuyerYes, MARGIN_X + 30, cursorY);
  addCheckboxField(doc, "NO", "firstHomeBuyerNo", formData.firstHomeBuyerNo, MARGIN_X + 40, cursorY);
  doc.text("Property Purpose:", MARGIN_X + 60, cursorY + 3.5);
  addCheckboxField(doc, "PPOR", "purposePPOR", formData.purposePPOR, MARGIN_X + 88, cursorY);
  addCheckboxField(doc, "Investment", "purposeDirectInvestment", formData.purposeDirectInvestment, MARGIN_X + 100, cursorY);
  addCheckboxField(doc, "SMSF", "purposeSMSF", formData.purposeSMSF, MARGIN_X + 119, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Est. Weekly Rental $:", "estimatedRental", formData.estimatedRental, MARGIN_X, cursorY + lo, propHalf);
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Property Manager:", MARGIN_X + propHalf + gap, cursorY + 3.5);
  addCheckboxField(doc, "YES", "propertyManagedYes", formData.propertyManagedYes, MARGIN_X + propHalf + gap + 30, cursorY);
  addCheckboxField(doc, "NO", "propertyManagedNo", formData.propertyManagedNo, MARGIN_X + propHalf + gap + 40, cursorY);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Property Manager Name:", "propertyManagerName", formData.propertyManagerName, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Property Manager Phone:", "propertyManagerPhone", formData.propertyManagerPhone, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  // ══════════════════════════════════════════
  // PAGE 2 — PROFESSIONAL CONTACTS + FINANCE + SMSF
  // ══════════════════════════════════════════
  doc.addPage();
  addPageHeader(doc, logoBase64, "Direct Investment & SMSF — Page 2 of 4");

  // ─── SECTION 4: PROFESSIONAL CONTACTS ───
  drawSectionHeading(doc, "SECTION 4 — PROFESSIONAL CONTACTS");

  doc.setFontSize(LABEL_FONT_SIZE + 0.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("Real Estate Agent", colAx, cursorY + 3);
  doc.text("Solicitor / Conveyancer", colBx, cursorY + 3);
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  addLabeledTextField(doc, "Agent Name:", "agentName", formData.agentName, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Solicitor Name:", "solicitorName", formData.solicitorName, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Agency:", "agentAgency", formData.agentAgency, colAx, cursorY + lo, colW);
  addLabeledTextField(doc, "Firm:", "solicitorFirm", formData.solicitorFirm, colBx, cursorY + lo, colW);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Phone:", "agentPhone", formData.agentPhone, colAx, cursorY + lo, half2);
  addLabeledTextField(doc, "Email:", "agentEmail", formData.agentEmail, colAx + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "Phone:", "solicitorPhone", formData.solicitorPhone, colBx, cursorY + lo, half2);
  addLabeledTextField(doc, "Email:", "solicitorEmail", formData.solicitorEmail, colBx + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE + 1);

  drawDivider(doc);

  // ─── SECTION 5: FINANCE & LOAN DETAILS ───
  drawSectionHeading(doc, "SECTION 5 — FINANCE & LOAN DETAILS");

  addLabeledTextField(doc, "Loan Amount Requested $:", "loanAmountRequested", formData.loanAmountRequested, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "LVR %:", "lvr", formData.lvr, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Loan Purpose:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "Owner-Occupied", "loanPurposeOO", formData.loanPurposeOO, MARGIN_X + 22, cursorY);
  addCheckboxField(doc, "Investment", "loanPurposeInvestment", formData.loanPurposeInvestment, MARGIN_X + 48, cursorY);
  doc.text("Repayment Type:", MARGIN_X + propHalf + gap, cursorY + 3.5);
  addCheckboxField(doc, "P&I", "loanTypePI", formData.loanTypePI, MARGIN_X + propHalf + gap + 28, cursorY);
  addCheckboxField(doc, "Int. Only", "loanTypeIO", formData.loanTypeIO, MARGIN_X + propHalf + gap + 40, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "I/O Period (yrs):", "ioPeriod", formData.ioPeriod, MARGIN_X, cursorY + lo, propSub5);
  addLabeledTextField(doc, "Loan Term (yrs):", "loanTerm", formData.loanTerm, MARGIN_X + propSub5 + 2, cursorY + lo, propSub5);
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Rate Type:", MARGIN_X + 2 * (propSub5 + 2) + 2, cursorY + 3.5);
  addCheckboxField(doc, "Variable", "rateVariable", formData.rateVariable, MARGIN_X + 2 * (propSub5 + 2) + 18, cursorY);
  addCheckboxField(doc, "Fixed", "rateFixed", formData.rateFixed, MARGIN_X + 2 * (propSub5 + 2) + 35, cursorY);
  addCheckboxField(doc, "Split", "rateSplit", formData.rateSplit, MARGIN_X + 2 * (propSub5 + 2) + 47, cursorY);
  addLabeledTextField(doc, "Fixed Period (yrs):", "fixedPeriod", formData.fixedPeriod, MARGIN_X + 3 * (propSub5 + 2) + 2, cursorY + lo, propSub5 + 4);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Offset Account:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "offsetYes", formData.offsetYes, MARGIN_X + 24, cursorY);
  addCheckboxField(doc, "NO", "offsetNo", formData.offsetNo, MARGIN_X + 34, cursorY);
  doc.text("Redraw Facility:", MARGIN_X + 55, cursorY + 3.5);
  addCheckboxField(doc, "YES", "redrawYes", formData.redrawYes, MARGIN_X + 78, cursorY);
  addCheckboxField(doc, "NO", "redrawNo", formData.redrawNo, MARGIN_X + 88, cursorY);
  addLabeledTextField(doc, "Lender Preference:", "lenderPreference", formData.lenderPreference, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  // Broker details
  doc.setFontSize(LABEL_FONT_SIZE + 0.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("Broker Details", MARGIN_X, cursorY + 3);
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  addLabeledTextField(doc, "Broker Name:", "brokerName", formData.brokerName, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Company:", "brokerCompany", formData.brokerCompany, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Phone:", "brokerPhone", formData.brokerPhone, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "ACL No.:", "brokerACL", formData.brokerACL, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE + 1);

  drawDivider(doc);

  // Credit History
  doc.setFontSize(LABEL_FONT_SIZE + 0.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("Credit History", MARGIN_X, cursorY + 3);
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Defaults / Judgements:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "defaultsYes", formData.defaultsYes, MARGIN_X + 38, cursorY);
  addCheckboxField(doc, "NO", "defaultsNo", formData.defaultsNo, MARGIN_X + 48, cursorY);
  doc.text("Bankruptcy / Part IX:", MARGIN_X + propHalf + gap, cursorY + 3.5);
  addCheckboxField(doc, "YES", "bankruptcyYes", formData.bankruptcyYes, MARGIN_X + propHalf + gap + 35, cursorY);
  addCheckboxField(doc, "NO", "bankruptcyNo", formData.bankruptcyNo, MARGIN_X + propHalf + gap + 45, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Default Details:", "defaultDetails", formData.defaultDetails, MARGIN_X, cursorY + lo, propHalf, 8);
  addLabeledTextField(doc, "Bankruptcy Details:", "bankruptcyDetails", formData.bankruptcyDetails, MARGIN_X + propHalf + gap, cursorY + lo, propHalf, 8);
  advanceCursor(13);

  drawDivider(doc);

  // ─── SECTION 6: SMSF DETAILS ───
  drawSectionHeading(doc, "SECTION 6 — SMSF DETAILS (Complete if SMSF purchase applies)");

  addLabeledTextField(doc, "SMSF Fund Name:", "smsfFundName", formData.smsfFundName, MARGIN_X, cursorY + lo, USABLE_W);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Fund ABN:", "smsfABN", formData.smsfABN, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Fund TFN:", "smsfTFN", formData.smsfTFN, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Trustee Structure:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "Individual Trustees", "smsfTrusteeIndividual", formData.smsfTrusteeIndividual, MARGIN_X + 30, cursorY);
  addCheckboxField(doc, "Corporate Trustee", "smsfTrusteeCorporate", formData.smsfTrusteeCorporate, MARGIN_X + 60, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Corporate Trustee Name:", "smsfCorpTrusteeName", formData.smsfCorpTrusteeName, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Corporate Trustee ACN:", "smsfCorpTrusteeACN", formData.smsfCorpTrusteeACN, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  // Members
  addLabeledTextField(doc, "Member 1 Name:", "smsfMember1Name", formData.smsfMember1Name, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Member 2 Name:", "smsfMember2Name", formData.smsfMember2Name, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "DOB:", "smsfMember1DOB", formData.smsfMember1DOB, MARGIN_X, cursorY + lo, half2);
  addLabeledTextField(doc, "Balance $:", "smsfMember1Balance", formData.smsfMember1Balance, MARGIN_X + half2 + 2, cursorY + lo, half2);
  addLabeledTextField(doc, "DOB:", "smsfMember2DOB", formData.smsfMember2DOB, MARGIN_X + propHalf + gap, cursorY + lo, half2);
  addLabeledTextField(doc, "Balance $:", "smsfMember2Balance", formData.smsfMember2Balance, MARGIN_X + propHalf + gap + half2 + 2, cursorY + lo, half2);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Total Fund Balance $:", "smsfTotalBalance", formData.smsfTotalBalance, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Annual Contributions $:", "smsfAnnualContributions", formData.smsfAnnualContributions, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("LRBA:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "smsfLRBAYes", formData.smsfLRBAYes, MARGIN_X + 12, cursorY);
  addCheckboxField(doc, "NO", "smsfLRBANo", formData.smsfLRBANo, MARGIN_X + 22, cursorY);
  doc.text("ATO Compliant:", MARGIN_X + 45, cursorY + 3.5);
  addCheckboxField(doc, "YES", "smsfCompliantYes", formData.smsfCompliantYes, MARGIN_X + 70, cursorY);
  addCheckboxField(doc, "NO", "smsfCompliantNo", formData.smsfCompliantNo, MARGIN_X + 80, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Bare Trust Name:", "smsfBareTrustName", formData.smsfBareTrustName, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Fund Auditor Name:", "smsfAuditorName", formData.smsfAuditorName, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  // ══════════════════════════════════════════
  // PAGE 3 — ASSETS + LIABILITIES + LIVING EXPENSES
  // ══════════════════════════════════════════
  doc.addPage();
  addPageHeader(doc, logoBase64, "Direct Investment & SMSF — Page 3 of 4");

  // ─── SECTION 7: ASSETS ───
  drawSectionHeading(doc, "SECTION 7 — ASSETS");

  // Asset table column layout
  const aLabelW = 40;
  const aGap = 2;
  const aValueW = 30;
  const aOwnerW = 60;
  const aRentW = USABLE_W - aLabelW - aValueW - aOwnerW - 3 * aGap;

  const ax0 = MARGIN_X;
  const ax1 = ax0 + aLabelW + aGap;
  const ax2 = ax1 + aValueW + aGap;
  const ax3 = ax2 + aOwnerW + aGap;

  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.setTextColor(255, 255, 255);
  doc.rect(ax1, cursorY, aValueW, 4.5, "F");
  doc.text("Value $", ax1 + aValueW / 2, cursorY + 3, { align: "center" });
  doc.rect(ax2, cursorY, aOwnerW, 4.5, "F");
  doc.text("Owner / Title", ax2 + aOwnerW / 2, cursorY + 3, { align: "center" });
  doc.rect(ax3, cursorY, aRentW, 4.5, "F");
  doc.text("Rental p.w. $", ax3 + aRentW / 2, cursorY + 3, { align: "center" });
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  const assetRows: { label: string; vf: string; vv: string; of: string; ov: string; rf?: string; rv?: string }[] = [
    { label: "PPOR / Home", vf: "assetPPORValue", vv: formData.assetPPORValue, of: "assetPPOROwner", ov: formData.assetPPOROwner, rf: "assetPPORRent", rv: formData.assetPPORRent },
    { label: "Investment Prop. 1", vf: "assetIP1Value", vv: formData.assetIP1Value, of: "assetIP1Owner", ov: formData.assetIP1Owner, rf: "assetIP1Rent", rv: formData.assetIP1Rent },
    { label: "Investment Prop. 2", vf: "assetIP2Value", vv: formData.assetIP2Value, of: "assetIP2Owner", ov: formData.assetIP2Owner, rf: "assetIP2Rent", rv: formData.assetIP2Rent },
    { label: "Investment Prop. 3", vf: "assetIP3Value", vv: formData.assetIP3Value, of: "assetIP3Owner", ov: formData.assetIP3Owner, rf: "assetIP3Rent", rv: formData.assetIP3Rent },
    { label: "SMSF Property", vf: "assetSMSFPropValue", vv: formData.assetSMSFPropValue, of: "assetSMSFPropOwner", ov: formData.assetSMSFPropOwner },
    { label: "Superannuation", vf: "assetSuperValue", vv: formData.assetSuperValue, of: "assetSuperOwner", ov: formData.assetSuperOwner },
    { label: "Shares / Managed Funds", vf: "assetSharesValue", vv: formData.assetSharesValue, of: "assetSharesOwner", ov: formData.assetSharesOwner },
    { label: "Savings / Cash", vf: "assetSavingsValue", vv: formData.assetSavingsValue, of: "assetSavingsOwner", ov: formData.assetSavingsOwner },
    { label: "Vehicle 1", vf: "assetVehicle1Value", vv: formData.assetVehicle1Value, of: "assetVehicle1Owner", ov: formData.assetVehicle1Owner },
    { label: "Vehicle 2", vf: "assetVehicle2Value", vv: formData.assetVehicle2Value, of: "assetVehicle2Owner", ov: formData.assetVehicle2Owner },
    { label: "Business Interests", vf: "assetBusinessValue", vv: formData.assetBusinessValue, of: "assetBusinessOwner", ov: formData.assetBusinessOwner },
    { label: "Other", vf: "assetOther1Value", vv: formData.assetOther1Value, of: "assetOther1Owner", ov: formData.assetOther1Owner },
    { label: "Other", vf: "assetOther2Value", vv: formData.assetOther2Value, of: "assetOther2Owner", ov: formData.assetOther2Owner },
  ];

  for (const row of assetRows) {
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(50, 50, 50);
    doc.text(row.label, ax0 + 1, cursorY + 3.5);
    addInlineTextField(doc, row.vf, row.vv, ax1, cursorY, aValueW);
    addInlineTextField(doc, row.of, row.ov, ax2, cursorY, aOwnerW);
    if (row.rf) {
      addInlineTextField(doc, row.rf, row.rv ?? "", ax3, cursorY, aRentW);
    } else {
      doc.setFillColor(240, 240, 240);
      doc.rect(ax3, cursorY, aRentW, FIELD_H, "F");
    }
    advanceCursor(FIELD_H + 1.5);
  }

  advanceCursor(2);
  drawDivider(doc);

  // ─── SECTION 8: LIABILITIES & LIVING EXPENSES ───
  drawSectionHeading(doc, "SECTION 8 — LIABILITIES & LIVING EXPENSES");

  // Liability table column layout
  const lLabelW = 32;
  const lGap = 2;
  const lLenderW = 40;
  const lBalW = 27;
  const lLimitW = 27;
  const lRepayW = USABLE_W - lLabelW - lLenderW - lBalW - lLimitW - 4 * lGap;

  const lx0 = MARGIN_X;
  const lx1 = lx0 + lLabelW + lGap;
  const lx2 = lx1 + lLenderW + lGap;
  const lx3 = lx2 + lBalW + lGap;
  const lx4 = lx3 + lLimitW + lGap;

  doc.setFontSize(6.5);
  doc.setFont("helvetica", "bold");
  doc.setFillColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.setTextColor(255, 255, 255);
  doc.rect(lx1, cursorY, lLenderW, 4.5, "F");
  doc.text("Lender", lx1 + lLenderW / 2, cursorY + 3, { align: "center" });
  doc.rect(lx2, cursorY, lBalW, 4.5, "F");
  doc.text("Balance $", lx2 + lBalW / 2, cursorY + 3, { align: "center" });
  doc.rect(lx3, cursorY, lLimitW, 4.5, "F");
  doc.text("Limit $", lx3 + lLimitW / 2, cursorY + 3, { align: "center" });
  doc.rect(lx4, cursorY, lRepayW, 4.5, "F");
  doc.text("Repayment $", lx4 + lRepayW / 2, cursorY + 3, { align: "center" });
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  type LiabRow = { label: string; lf: string; lv: string; bf: string; bv: string; lif: string; liv: string; rf: string; rv: string; noLender?: boolean };
  const liabRows: LiabRow[] = [
    { label: "Mortgage 1", lf: "liabMortgage1Lender", lv: formData.liabMortgage1Lender, bf: "liabMortgage1Balance", bv: formData.liabMortgage1Balance, lif: "liabMortgage1Limit", liv: formData.liabMortgage1Limit, rf: "liabMortgage1Repayment", rv: formData.liabMortgage1Repayment },
    { label: "Mortgage 2", lf: "liabMortgage2Lender", lv: formData.liabMortgage2Lender, bf: "liabMortgage2Balance", bv: formData.liabMortgage2Balance, lif: "liabMortgage2Limit", liv: formData.liabMortgage2Limit, rf: "liabMortgage2Repayment", rv: formData.liabMortgage2Repayment },
    { label: "Mortgage 3", lf: "liabMortgage3Lender", lv: formData.liabMortgage3Lender, bf: "liabMortgage3Balance", bv: formData.liabMortgage3Balance, lif: "liabMortgage3Limit", liv: formData.liabMortgage3Limit, rf: "liabMortgage3Repayment", rv: formData.liabMortgage3Repayment },
    { label: "Car Loan", lf: "liabCarLender", lv: formData.liabCarLender, bf: "liabCarBalance", bv: formData.liabCarBalance, lif: "liabCarLimit", liv: formData.liabCarLimit, rf: "liabCarRepayment", rv: formData.liabCarRepayment },
    { label: "Personal Loan", lf: "liabPersonalLender", lv: formData.liabPersonalLender, bf: "liabPersonalBalance", bv: formData.liabPersonalBalance, lif: "liabPersonalLimit", liv: formData.liabPersonalLimit, rf: "liabPersonalRepayment", rv: formData.liabPersonalRepayment },
    { label: "Credit Card 1", lf: "liabCC1Lender", lv: formData.liabCC1Lender, bf: "liabCC1Balance", bv: formData.liabCC1Balance, lif: "liabCC1Limit", liv: formData.liabCC1Limit, rf: "liabCC1Repayment", rv: formData.liabCC1Repayment },
    { label: "Credit Card 2", lf: "liabCC2Lender", lv: formData.liabCC2Lender, bf: "liabCC2Balance", bv: formData.liabCC2Balance, lif: "liabCC2Limit", liv: formData.liabCC2Limit, rf: "liabCC2Repayment", rv: formData.liabCC2Repayment },
    { label: "HECS / HELP", lf: "liabHECSLender", lv: "", bf: "liabHECSBalance", bv: formData.liabHECSBalance, lif: "liabHECSLimit", liv: "", rf: "liabHECSRepayment", rv: formData.liabHECSRepayment, noLender: true },
    { label: "Priv. Health Ins.", lf: "liabPrivateHealthLender", lv: "", bf: "liabPrivateHealthBalance", bv: "", lif: "liabPrivateHealthLimit", liv: "", rf: "liabPrivateHealthRepayment", rv: formData.liabPrivateHealthRepayment, noLender: true },
    { label: "Priv. School Fees", lf: "liabPrivateSchoolLender", lv: "", bf: "liabPrivateSchoolBalance", bv: "", lif: "liabPrivateSchoolLimit", liv: "", rf: "liabPrivateSchoolRepayment", rv: formData.liabPrivateSchoolRepayment, noLender: true },
    { label: "Child Care", lf: "liabChildCareLender", lv: "", bf: "liabChildCareBalance", bv: "", lif: "liabChildCareLimit", liv: "", rf: "liabChildCareRepayment", rv: formData.liabChildCareRepayment, noLender: true },
    { label: "Other", lf: "liabOther1Lender", lv: formData.liabOther1Lender, bf: "liabOther1Balance", bv: formData.liabOther1Balance, lif: "liabOther1Limit", liv: formData.liabOther1Limit, rf: "liabOther1Repayment", rv: formData.liabOther1Repayment },
    { label: "Other", lf: "liabOther2Lender", lv: formData.liabOther2Lender, bf: "liabOther2Balance", bv: formData.liabOther2Balance, lif: "liabOther2Limit", liv: formData.liabOther2Limit, rf: "liabOther2Repayment", rv: formData.liabOther2Repayment },
  ];

  for (const row of liabRows) {
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(50, 50, 50);
    doc.text(row.label, lx0 + 1, cursorY + 3.5);
    if (row.noLender) {
      doc.setFillColor(240, 240, 240);
      doc.rect(lx1, cursorY, lLenderW + lGap + lBalW + lGap + lLimitW, FIELD_H, "F");
      addInlineTextField(doc, row.rf, row.rv, lx4, cursorY, lRepayW);
    } else {
      addInlineTextField(doc, row.lf, row.lv, lx1, cursorY, lLenderW);
      addInlineTextField(doc, row.bf, row.bv, lx2, cursorY, lBalW);
      addInlineTextField(doc, row.lif, row.liv, lx3, cursorY, lLimitW);
      addInlineTextField(doc, row.rf, row.rv, lx4, cursorY, lRepayW);
    }
    advanceCursor(FIELD_H + 1.5);
  }

  advanceCursor(2);

  // Monthly Living Expenses sub-section
  doc.setFontSize(LABEL_FONT_SIZE + 0.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("Monthly Living Expenses", MARGIN_X, cursorY + 3);
  doc.setTextColor(0, 0, 0);
  advanceCursor(5);

  // 2 columns of expense rows
  const expW = (USABLE_W - colGap) / 2 - 30;
  const expRows = [
    { label: "Groceries / Food:", f: "expGroceries", v: formData.expGroceries },
    { label: "Transport / Car:", f: "expTransport", v: formData.expTransport },
    { label: "Utilities:", f: "expUtilities", v: formData.expUtilities },
    { label: "Phone / Internet:", f: "expPhoneInternet", v: formData.expPhoneInternet },
    { label: "Insurance:", f: "expInsurance", v: formData.expInsurance },
    { label: "Medical / Health:", f: "expMedical", v: formData.expMedical },
    { label: "Entertainment:", f: "expEntertainment", v: formData.expEntertainment },
    { label: "Education:", f: "expEducation", v: formData.expEducation },
    { label: "Other Living:", f: "expOtherMonthly", v: formData.expOtherMonthly },
  ];

  for (let i = 0; i < expRows.length; i += 2) {
    const rowA = expRows[i];
    const rowB = expRows[i + 1];
    addLabeledTextField(doc, rowA.label, rowA.f, rowA.v, MARGIN_X, cursorY + lo, expW + 30);
    if (rowB) {
      addLabeledTextField(doc, rowB.label, rowB.f, rowB.v, MARGIN_X + propHalf + gap, cursorY + lo, expW + 30);
    }
    advanceCursor(ROW_ADVANCE);
  }

  // ══════════════════════════════════════════
  // PAGE 4 — INSURANCE + UTILITIES/SOLAR + DOCUMENTS + SIGNATURES
  // ══════════════════════════════════════════
  doc.addPage();
  addPageHeader(doc, logoBase64, "Direct Investment & SMSF — Page 4 of 4");

  // ─── SECTION 9: INSURANCE ───
  drawSectionHeading(doc, "SECTION 9 — INSURANCE");

  const insGap = 3;
  const insW2 = (USABLE_W - insGap) / 2;

  // Row 1: Building Insurance
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Building Insurance:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "buildingInsYes", formData.buildingInsYes, MARGIN_X + 32, cursorY);
  addCheckboxField(doc, "NO", "buildingInsNo", formData.buildingInsNo, MARGIN_X + 42, cursorY);
  doc.text("Contents Insurance:", MARGIN_X + insW2 + insGap, cursorY + 3.5);
  addCheckboxField(doc, "YES", "contentsInsYes", formData.contentsInsYes, MARGIN_X + insW2 + insGap + 32, cursorY);
  addCheckboxField(doc, "NO", "contentsInsNo", formData.contentsInsNo, MARGIN_X + insW2 + insGap + 42, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Insurer:", "buildingInsurer", formData.buildingInsurer, MARGIN_X, cursorY + lo, insW2 / 2 - 1);
  addLabeledTextField(doc, "Policy No.:", "buildingPolicyNo", formData.buildingPolicyNo, MARGIN_X + insW2 / 2 + 1, cursorY + lo, insW2 / 2 - 1);
  addLabeledTextField(doc, "Insurer:", "contentsInsurer", formData.contentsInsurer, MARGIN_X + insW2 + insGap, cursorY + lo, insW2 / 2 - 1);
  addLabeledTextField(doc, "Policy No.:", "contentsPolicyNo", formData.contentsPolicyNo, MARGIN_X + insW2 + insGap + insW2 / 2 + 1, cursorY + lo, insW2 / 2 - 1);
  advanceCursor(ROW_ADVANCE);

  addLabeledTextField(doc, "Sum Insured $:", "buildingSum", formData.buildingSum, MARGIN_X, cursorY + lo, insW2);
  advanceCursor(ROW_ADVANCE);

  // Row 2: Landlord + Life Insurance
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Landlord Insurance:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "landlordInsYes", formData.landlordInsYes, MARGIN_X + 30, cursorY);
  addCheckboxField(doc, "NO", "landlordInsNo", formData.landlordInsNo, MARGIN_X + 40, cursorY);
  doc.text("Life Insurance:", MARGIN_X + insW2 + insGap, cursorY + 3.5);
  addCheckboxField(doc, "YES", "lifeInsYes", formData.lifeInsYes, MARGIN_X + insW2 + insGap + 24, cursorY);
  addCheckboxField(doc, "NO", "lifeInsNo", formData.lifeInsNo, MARGIN_X + insW2 + insGap + 34, cursorY);
  advanceCursor(7);

  addLabeledTextField(doc, "Insurer:", "landlordInsurer", formData.landlordInsurer, MARGIN_X, cursorY + lo, insW2 / 2 - 1);
  addLabeledTextField(doc, "Policy No.:", "landlordPolicyNo", formData.landlordPolicyNo, MARGIN_X + insW2 / 2 + 1, cursorY + lo, insW2 / 2 - 1);
  addLabeledTextField(doc, "Insurer:", "lifeInsurer", formData.lifeInsurer, MARGIN_X + insW2 + insGap, cursorY + lo, insW2 / 2 - 1);
  addLabeledTextField(doc, "Sum Insured $:", "lifeSumInsured", formData.lifeSumInsured, MARGIN_X + insW2 + insGap + insW2 / 2 + 1, cursorY + lo, insW2 / 2 - 1);
  advanceCursor(ROW_ADVANCE);

  // Income Protection
  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Income Protection:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES", "incomeProtYes", formData.incomeProtYes, MARGIN_X + 30, cursorY);
  addCheckboxField(doc, "NO", "incomeProtNo", formData.incomeProtNo, MARGIN_X + 40, cursorY);
  addLabeledTextField(doc, "Insurer:", "incomeProtInsurer", formData.incomeProtInsurer, MARGIN_X + insW2 + insGap, cursorY + lo, insW2);
  advanceCursor(ROW_ADVANCE + 1);

  drawDivider(doc);

  // ─── SECTION 10: UTILITIES & ENERGY ───
  drawSectionHeading(doc, "SECTION 10 — UTILITIES & ENERGY");

  addLabeledTextField(doc, "Electricity Provider:", "electricityProvider", formData.electricityProvider, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Avg. Electricity Bill (monthly) $:", "electricityBillMonthly", formData.electricityBillMonthly, MARGIN_X + propHalf + gap, cursorY + lo, propHalf);
  advanceCursor(ROW_ADVANCE);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Water Filter Installed:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES — Has Water Filter", "hasWaterFilter", formData.hasWaterFilter, MARGIN_X + 32, cursorY);
  addCheckboxField(doc, "NO — Does Not Have", "noWaterFilter", formData.noWaterFilter, MARGIN_X + 80, cursorY);
  advanceCursor(8);

  // Solar Cross-Sell Box
  doc.setFillColor(SOLAR_BG[0], SOLAR_BG[1], SOLAR_BG[2]);
  doc.rect(MARGIN_X, cursorY, USABLE_W, 22, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("CONSULTANT PROMPT — One Solar Australia Cross-Sell", MARGIN_X + 3, cursorY + 5);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "italic");
  const solarText = "\"So many of our clients have been talking about getting solar through our sister company One Solar Australia. I'd love to squeeze you into one of our solar consultant's schedules — there's a lot you could be saving and putting straight back into your wealth strategy. Can I book a quick appointment for you?\"";
  const solarLines = doc.splitTextToSize(solarText, USABLE_W - 6);
  doc.text(solarLines, MARGIN_X + 3, cursorY + 10);
  doc.setTextColor(0, 0, 0);
  advanceCursor(24);

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Client Interested in Solar Consultation:", MARGIN_X, cursorY + 3.5);
  addCheckboxField(doc, "YES — Book Appointment", "solarInterestYes", formData.solarInterestYes, MARGIN_X + 62, cursorY);
  addCheckboxField(doc, "NO — Not at this time", "solarInterestNo", formData.solarInterestNo, MARGIN_X + 105, cursorY);
  advanceCursor(8);

  addLabeledTextField(doc, "Name for Appointment:", "solarApptName", formData.solarApptName, MARGIN_X, cursorY + lo, propHalf);
  addLabeledTextField(doc, "Phone:", "solarApptPhone", formData.solarApptPhone, MARGIN_X + propHalf + gap, cursorY + lo, propHalf / 2);
  addLabeledTextField(doc, "Preferred Time:", "solarApptPreferredTime", formData.solarApptPreferredTime, MARGIN_X + propHalf + gap + propHalf / 2 + gap, cursorY + lo, propHalf / 2 - gap);
  advanceCursor(ROW_ADVANCE + 1);

  drawDivider(doc);

  // ─── SECTION 11: DOCUMENT CHECKLIST ───
  drawSectionHeading(doc, "SECTION 11 — DOCUMENT CHECKLIST");

  const docCols = [
    {
      title: "Income & Identity",
      items: [
        { label: "Last 2 payslips (both applicants)", f: "docPayslips", v: formData.docPayslips },
        { label: "Last 2 years tax returns + NOA", f: "doc2YrTaxReturns", v: formData.doc2YrTaxReturns },
        { label: "3 months bank statements", f: "doc3MthBankStatements", v: formData.doc3MthBankStatements },
        { label: "Identity verified (Passport / Driver Licence)", f: "docIDVerified", v: formData.docIDVerified },
        { label: "Centrelink statement (if applicable)", f: "docCentrelink", v: formData.docCentrelink },
        { label: "Rental statements (12 months)", f: "docRentalStatement", v: formData.docRentalStatement },
      ],
    },
    {
      title: "Property & SMSF Documents",
      items: [
        { label: "Contract of Sale / Purchase", f: "docContractOfSale", v: formData.docContractOfSale },
        { label: "Building inspection report", f: "docBuildingReport", v: formData.docBuildingReport },
        { label: "Pest inspection report", f: "docPestReport", v: formData.docPestReport },
        { label: "Council rates notice", f: "docCouncilRates", v: formData.docCouncilRates },
        { label: "Body corporate statements (if applicable)", f: "docBodyCorp", v: formData.docBodyCorp },
        { label: "SMSF Trust Deed (current)", f: "docSmsfDeed", v: formData.docSmsfDeed },
        { label: "Latest SMSF Audit Letter", f: "docSmsfAuditLetter", v: formData.docSmsfAuditLetter },
        { label: "Trustee Resolution to Borrow", f: "docSmsfTrusteeResolution", v: formData.docSmsfTrusteeResolution },
        { label: "Bare Trust Deed", f: "docBareTrustDeed", v: formData.docBareTrustDeed },
      ],
    },
  ];

  const docColX = [MARGIN_X, MARGIN_X + colW + colGap];
  for (let c = 0; c < docCols.length; c++) {
    const col = docCols[c];
    const cx = docColX[c];
    const savedY = cursorY;
    doc.setFontSize(LABEL_FONT_SIZE);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
    doc.text(col.title, cx, cursorY + 3);
    doc.setTextColor(0, 0, 0);
    let colCursorY = cursorY + 5;
    for (const item of col.items) {
      const cbSize = 3.5;
      doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
      doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
      doc.setLineWidth(0.2);
      doc.rect(cx, colCursorY, cbSize, cbSize, "FD");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cb = new (doc as any).AcroFormCheckBox();
      cb.fieldName = item.f;
      cb.x = cx;
      cb.y = colCursorY;
      cb.width = cbSize;
      cb.height = cbSize;
      if (item.v) {
        cb.appearanceState = "On"; cb.value = "On"; cb.defaultValue = "On";
      } else {
        cb.appearanceState = "Off"; cb.value = "Off"; cb.defaultValue = "Off";
      }
      doc.addField(cb);
      doc.setFontSize(6.5);
      doc.setFont("helvetica", "normal");
      doc.text(item.label, cx + cbSize + 2, colCursorY + 2.8);
      colCursorY += 5.5;
    }
    if (c === 0) {
      cursorY = savedY;
    } else {
      cursorY = Math.max(cursorY, colCursorY) + 3;
    }
  }

  drawDivider(doc);

  // ─── PRIVACY ACT ───
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(HEADING_BG[0], HEADING_BG[1], HEADING_BG[2]);
  doc.text("PRIVACY ACT & DECLARATION", MARGIN_X, cursorY + 5);
  doc.setTextColor(0, 0, 0);
  advanceCursor(8);

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  const privacyText = "I/We declare that the information provided in this form is true and correct to the best of my/our knowledge and belief. I/We authorise One Property Australia and its authorised representatives to collect, use, and disclose this information to lenders, mortgage brokers, legal practitioners, and other parties as necessary to assess, process, and complete this property transaction. All information will be handled in accordance with the Privacy Act 1988 (Cth).";
  const privacyLines = doc.splitTextToSize(privacyText, USABLE_W);
  doc.text(privacyLines, MARGIN_X, cursorY + 4);
  advanceCursor(14);

  drawDivider(doc);

  // ─── SIGNATURES ───
  const sigColW = (USABLE_W - colGap) / 2;
  const sigH = 16;

  doc.setFontSize(LABEL_FONT_SIZE);
  doc.setFont("helvetica", "bold");
  doc.text("Signature (Applicant A):", MARGIN_X, cursorY + 3);
  doc.text("Signature (Applicant B):", MARGIN_X + sigColW + colGap, cursorY + 3);
  advanceCursor(5);

  doc.setFillColor(FIELD_BG[0], FIELD_BG[1], FIELD_BG[2]);
  doc.setDrawColor(FIELD_BORDER[0], FIELD_BORDER[1], FIELD_BORDER[2]);
  doc.setLineWidth(0.2);
  doc.rect(MARGIN_X, cursorY, sigColW, sigH, "FD");
  doc.rect(MARGIN_X + sigColW + colGap, cursorY, sigColW, sigH, "FD");
  advanceCursor(sigH + 4);

  const sig3W = (USABLE_W - 2 * gap) / 3;
  addLabeledTextField(doc, "Consultant Name:", "consultantName", formData.consultantName, MARGIN_X, cursorY + lo, sig3W);
  addLabeledTextField(doc, "Consultant No. / ACL:", "consultantNumber", formData.consultantNumber, MARGIN_X + sig3W + gap, cursorY + lo, sig3W);
  addLabeledTextField(doc, "Date:", "date", formData.date, MARGIN_X + 2 * (sig3W + gap), cursorY + lo, sig3W);

  doc.save("OPA_Property_Transaction_Form.pdf");
}
